import React from 'react';
import { getLocationFacilityDetails } from '../../utils/constants';
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';

export const BreastFollowupTab = ({
    followupReason,
    setFollowupReason,
    followupTo,
    setFollowupTo,
    durationDays,
    durationWeeks,
    durationMonths,
    handleDurationChange,
    dueDate,
    errors,
    setErrors,
    followupHistory,
    getFacilityLabel,
    handleSaveFollowup,
    facilityType
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', fontSize: '18px' }}>
                Follow Up
            </Typography>

            {/* Flex-based Follow Up Tab Form */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px', mt: 2, width: '100%' }}>
                {/* Reason for Follow Up */}
                <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
                    <TextField
                        variant="standard"
                        label="Reason for Follow-up"
                        placeholder="Reason for Follow-up"
                        value={followupReason}
                        onChange={(e) => {
                            setFollowupReason(e.target.value);
                            if (errors.followupReason) {
                                setErrors(prev => ({ ...prev, followupReason: null }));
                            }
                        }}
                        error={!!errors.followupReason}
                        helperText={errors.followupReason}
                        fullWidth
                        required
                    />
                </Box>

                {/* Follow Up To select */}
                <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
                    <FormControl variant="standard" fullWidth required error={!!errors.followupTo}>
                        <InputLabel>Follow-up to</InputLabel>
                        <Select
                            value={followupTo}
                            onChange={(e) => {
                                setFollowupTo(e.target.value);
                                if (errors.followupTo) {
                                    setErrors(prev => ({ ...prev, followupTo: null }));
                                }
                            }}
                            label="Follow-up to"
                        >
                            <MenuItem value="">None</MenuItem>
                            {(() => {
                                const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails") || localStorage.getItem("NCD_loggedinUserdetails") || "{}");
                                const { userToList } = getLocationFacilityDetails(loggedData);
                                return userToList.map((item) => (
                                    <MenuItem key={item.facilityId} value={item.name === 'Self' ? (facilityType || String(item.facilityId)) : String(item.facilityId)}>
                                        {item.name}
                                    </MenuItem>
                                ));
                            })()}
                        </Select>
                        {errors.followupTo && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.followupTo}
                            </Typography>
                        )}
                    </FormControl>
                </Box>

                {/* Follow Up Duration: Days, Weeks, Months side by side */}
                <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: !!errors.duration ? 'error.main' : '#334155', mb: 1 }}>
                        Follow-up Duration *
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
                        <TextField
                            variant="standard"
                            type="number"
                            value={durationDays}
                            onChange={(e) => handleDurationChange('days', e.target.value)}
                            helperText={errors.durationDays || "Days"}
                            error={!!errors.durationDays}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            variant="standard"
                            type="number"
                            value={durationWeeks}
                            onChange={(e) => handleDurationChange('weeks', e.target.value)}
                            helperText={errors.durationWeeks || "Weeks"}
                            error={!!errors.durationWeeks}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            variant="standard"
                            type="number"
                            value={durationMonths}
                            onChange={(e) => handleDurationChange('months', e.target.value)}
                            helperText={errors.durationMonths || "Months"}
                            error={!!errors.durationMonths}
                            sx={{ flex: 1 }}
                        />
                    </Box>
                    {errors.duration && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {errors.duration}
                        </Typography>
                    )}
                </Box>

                {/* Due Date read-only field */}
                <Box sx={{ width: { xs: '100%', md: 'calc(50% - 12px)' } }}>
                    <TextField
                        variant="standard"
                        label="Due Date"
                        value={dueDate ? dayjs(dueDate, 'YYYY-MM-DD').format('DD-MM-YYYY') : ''}
                        fullWidth
                        InputProps={{
                            readOnly: true,
                        }}
                        error={!!errors.dueDate}
                        helperText={errors.dueDate}
                    />
                </Box>
            </Box>

            {/* Follow Up Save button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 4 }}>
                <Button
                    onClick={handleSaveFollowup}
                    variant="contained"
                    sx={{
                        bgcolor: '#3f51b5',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 4.5,
                        py: 1,
                        borderRadius: '4px',
                        '&:hover': { bgcolor: '#303f9f' }
                    }}
                >
                    SAVE
                </Button>
            </Box>

            {/* Follow Up History table */}
            {followupHistory && followupHistory.length > 0 && (
                <Box sx={{ mt: 5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', mb: 2 }}>
                        Follow Up History
                    </Typography>
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table table-bordered" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#eaeff7', fontWeight: 'bold' }}>
                                    <th style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>Follow-By</th>
                                    <th style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>Follow-up To</th>
                                    <th style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>Date</th>
                                    <th style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>Doctor Username</th>
                                    <th style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>Reason for Follow-up</th>
                                    <th style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>Due Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {followupHistory.map((item, idx) => (
                                    <tr key={item.followUpId || idx}>
                                        <td style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>{getFacilityLabel(item.fromFacility)}</td>
                                        <td style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>{getFacilityLabel(item.toFacility)}</td>
                                        <td style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>{item.date || ''}</td>
                                        <td style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>{item.fromUser || ''}</td>
                                        <td style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>{item.followUpReason || ''}</td>
                                        <td style={{ padding: '12px 8px', border: '1px solid #cbd5e1' }}>{item.followUpDueDate || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Box>
            )}
        </Box>
    );
};
